<template>
  <div class="container-fluid">
    <div class="row">
        <div class="col-12">
          <form id="rfq_form" @submit.prevent="confirm">
            <div class="card">
                <div class="card-header pb-0">
                    <div class="d-flex justify-content-between">
                        <div class="d-grid gap-2 d-flex">
                            <button 
                                class="btn btn-xs btn-outline-secondary text-xs font-weight-normal m-0" 
                                @click="edit = true"
                                v-if="!edit && rfq && rfq.state == 'Publish on Portal' && rfq.need_supplier_confirm">
                                <i class="fa fa-pencil-square-o" aria-hidden="true"></i> Edit
                            </button>

                            <button 
                                type="button"
                                @click="download"
                                :class="'btn btn-xs btn-outline-primary text-xs font-weight-normal m-0 ' + (loading ? 'disabled' : '')">
                                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true" v-if="loading"></span>
                                <i class="fa fa-download" v-if="!loading"></i> Download RFQ
                            </button>
                            <template v-if="edit">
                                <button type="submit" :class="'btn btn-xs btn-primary text-xs font-weight-normal m-0 ' + (loading ? 'disabled' : '')">
                                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true" v-if="loading"></span>
                                    <i class="fa fa-check" aria-hidden="true" v-if="!loading"></i> 
                                    Confirm
                                </button>
                                <button @click="edit = false" class="btn btn-xs btn-outline-secondary text-xs font-weight-normal m-0">
                                    <i class="fa fa-times" aria-hidden="true"></i> Discard
                                </button>
                            </template>
                        </div>
                        <div class="d-grid gap-2 d-flex">
                            <!-- <argon-badge color="warning" class="text-xxs align-self-center" v-if="rfq && rfq.state != 'Confirmed Supplier'">Waiting Confirmation</argon-badge>
                            <argon-badge color="info" class="text-xxs align-self-center" v-if="rfq && rfq.state == 'Confirmed Supplier'">{{ rfq.state }}</argon-badge> -->
                            <argon-badge class="align-self-center bg-primary text-white" size="sm" v-if="rfq && rfq.state == 'RFQ'">{{ rfq.state }}</argon-badge>
                            <argon-badge color="primary" class="align-self-center" size="sm" v-else-if="rfq && rfq.state == 'Submitted'">{{ rfq.state }}</argon-badge>
                            <argon-badge color="warning" class="align-self-center" size="sm" v-else>Waiting Confirmation</argon-badge>
                        </div>
                    </div>
                    <hr/>
                </div>
                <div class="card-body min-vh-50 pt-0" v-if="rfq">
                    <div class="d-flex justify-content-between">
                      <h5>{{ rfq.rfq_number === 'New' ? rfq.name : rfq.rfq_number }}</h5>
                      <div>
                        <argon-badge color="danger" size="sm" v-if="rfq.bypass_kanban > 0">{{ rfq.kanban_msg }}</argon-badge>
                      </div>
                    </div>
                    <div class="row">
                        <div class="col-sm-6">
                            <table class="text-sm" width="100%">
                                <tbody>
                                    <tr>
                                        <td class="text-secondary align-top" width="30%">Vendor</td>
                                        <td class="font-weight-bold align-top">{{ rfq.partner_name }}</td>
                                    </tr>
                                    <tr>
                                        <td class="text-secondary align-top">Vendor Reference</td>
                                        <td class="font-weight-bold align-top">{{ rfq.vendor_reference }}</td>
                                    </tr>
                                    <tr>
                                        <td class="text-secondary align-top">PO Number</td>
                                        <td class="font-weight-bold align-top">{{ rfq.rfq_number === 'New' ? '-' : rfq.name }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div class="col-sm-6">
                            <table class="text-sm" width="100%">
                                <tbody>
                                    <tr>
                                        <td class="text-secondary align-top" width="30%">Order Date</td>
                                        <td class="font-weight-bold align-top">{{ rfq.date_order }}</td>
                                    </tr>
                                    <tr v-if="edit">
                                        <td class="text-secondary align-top">Receipt Date</td>
                                        <td class="font-weight-bold align-top">
                                          <Datepicker
                                            v-model="receipt_date" 
                                            format="dd MMMM yyyy"
                                            />
                                        </td>
                                    </tr>
                                    <tr v-else>
                                        <td class="text-secondary align-top">Receipt Date</td>
                                        <td class="font-weight-bold align-top">{{ rfq.date_planned }}</td>
                                    </tr>
                                    <tr>
                                        <td class="text-secondary align-top">Vendor Forecast</td>
                                        <td class="font-weight-bold align-top">{{ rfq.vendor_forecast }}</td>
                                    </tr>
                                    <tr>
                                        <td class="text-secondary align-top">Projects</td>
                                        <td class="font-weight-bold align-top">
                                          <p class="text-sm mb-0 pl-1">
                                            <argon-badge style="margin-left: 2px;" v-for="project in rfq.project_ids" :key="project.id" 
                                              color="secondary"
                                              size="sm">{{ project.name }}
                                          </argon-badge>
                                          </p>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div class="table-responsive p-0 mt-3">
                        <table class="table align-items-center mb-0">
                            <thead>
                                <tr class="bg-light">
                                    <th class="text-uppercase text-xs font-weight-bolder opacity-7 ps-2">#</th>
                                    <th class="text-uppercase text-xs font-weight-bolder opacity-7 ps-2">Product</th>
                                    <th class="text-uppercase text-xs font-weight-bolder opacity-7 ps-2">Description</th>
                                    <th class="text-uppercase text-xs font-weight-bolder opacity-7 ps-2">Projects</th>
                                    <th class="text-uppercase text-xs font-weight-bolder opacity-7 ps-2">Quantity</th>
                                    <th class="text-uppercase text-xs font-weight-bolder opacity-7 ps-2">Quantity Available</th>
                                    <th class="text-uppercase text-xs font-weight-bolder opacity-7 ps-2">Unit Price</th>
                                    <th class="text-uppercase text-xs font-weight-bolder opacity-7 ps-2">Bid Price</th>
                                    <th class="text-uppercase text-xs font-weight-bolder opacity-7 ps-2">Taxes</th>
                                    <th class="text-uppercase text-xs font-weight-bolder opacity-7 ps-2">Subtotal</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="line,i in rfq.order_line" :key="line.id">
                                    <td class="text-sm mb-0 ps-2">{{ i+1 }}</td>
                                    <td>
                                        <p class="text-sm mb-0">{{ line.name }}</p>
                                    </td>
                                    <td>
                                        <p class="text-sm mb-0">{{ line.name }}</p>
                                    </td>
                                    <td>
                                      <span class="text-sm mb-0 pl-1"><argon-badge style="margin-left: 2px;" v-for="project in line.project_ids" :key="project.id" 
                                        color="secondary"
                                        size="sm">{{ project.name }}
                                      </argon-badge></span>
                                      <span @click="showLineProjectDetail(line.id)" v-if="line.project_ids && line.project_ids.length > 0" style="margin-left: 5px;cursor: pointer;"
                                        data-bs-toggle="modal" data-bs-target="#projectPurchaseLine">
                                        <i class="fa fa-info-circle" aria-hidden="true"></i>
                                      </span>
                                    </td>
                                    <td>
                                        <p class="text-sm mb-0">{{ line.product_qty }}</p>
                                    </td>
                                    <td>
                                        <input 
                                          :ref="'qtyAvail' + line.id" 
                                          class="form-control form-control-sm mb-0" 
                                          type="number"
                                          v-if="edit"/>
                                        <p class="text-sm mb-0" v-else>{{line.qty_avail}}</p>
                                    </td>
                                    <td>
                                        <p class="text-sm text-end mb-0">{{ line.price_unit }}</p>
                                    </td>
                                    <td>
                                        <input 
                                          :ref="'bidPrice' + line.id" 
                                          class="form-control form-control-sm mb-0" 
                                          type="number" 
                                          v-if="edit"/>
                                        <p class="text-sm mb-0" v-else>{{line.bid_price}}</p>
                                    </td>
                                    <td>
                                        <p class="text-sm text-end mb-0" v-for="tax,i in line.taxes_id" :key="i">{{ tax }}</p>
                                    </td>
                                    <td>
                                        <p class="text-sm text-end mb-0 px-2">{{ line.price_subtotal }}</p>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <!-- modal -->
                    <div class="modal fade" id="projectPurchaseLine" tabindex="-1" aria-labelledby="projectPurchaseLinelabel" aria-hidden="true">
                      <div class="modal-dialog modal-xl modal-dialog-scrollable">
                        <div class="modal-content">
                          <div class="modal-header">
                            <h5 class="modal-title" id="projectPurchaseLinelabel">RFQ Line Project List</h5>
                            <button type="button" class="btn btn-light" data-bs-dismiss="modal">X</button>
                          </div>
                          <div class="modal-body">
                            <div class="table-responsive p-0 mt-3">
                              <table class="table align-items-center mb-0">
                                  <thead>
                                      <tr class="bg-light">
                                          <th class="text-uppercase text-xs font-weight-bolder opacity-7 ps-2">#</th>
                                          <th class="text-uppercase text-xs font-weight-bolder opacity-7 ps-2">Product</th>
                                          <th class="text-uppercase text-xs font-weight-bolder opacity-7 ps-2">Projects</th>
                                          <th class="text-uppercase text-xs font-weight-bolder opacity-7 ps-2">Quantity</th>
                                      </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="line,i in modal.project_rfq_line" :key="line.id">
                                      <td class="text-sm mb-0 ps-2">{{ i+1 }}</td>
                                      <td class="text-sm mb-0 ps-2">{{ line.product_name }}</td>
                                      <td class="text-sm mb-0 ps-2">{{ line.project_name }}</td>
                                      <td class="text-sm mb-0 ps-2">{{ line.product_qty }}</td>
                                    </tr>
                                  </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row mt-3">
                        <div class="col-md offset-md-8 px-4">
                            <table class="text-sm font-weight-bold" width="100%">
                                <tbody>
                                    <tr>
                                        <td class="" width="50%">Untaxed Amount</td>
                                        <td class="text-end">{{ rfq.amount_untaxed }}</td>
                                    </tr>   
                                    <tr>
                                        <td class="">Amount Tax</td>
                                        <td class="text-end">{{ rfq.amount_tax }}</td>
                                    </tr>
                                    <tr>
                                        <td class="border-top">Amount Total</td>
                                        <td class="border-top font-weight-bold text-end">{{ rfq.amount_total }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
          </form>
        </div>
    </div>
  </div>
</template>

<script>
import ArgonBadge from '../components/ArgonBadge.vue'
const endpoint = require("../router/endpoint");

export default {
  name: "rfq-detail",
  data() {
    return {
      rfq: null,
      loading: false,
      receipt_date: null,
      edit: false, // true = editable, false = readonly
      order_line: [],
      modal: {
        project_rfq_line: null,
      },
    };
  },
  components: {
    ArgonBadge
  },
  methods: {
    showLineProjectDetail(lineId){
      this.axios
        .post(
          endpoint.PURCHASE_PROJECT_LIST,
          {
            purchase_line_id: lineId
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`
            }
          }
        )
        .then((response) => {
          if (response.data.response_code === "0000") {
            this.modal.project_rfq_line = response.data.data;
            console.log(response.data.data)
          } else {
            this.$notify({
                type: 'warn',
                title: 'Warning',
                text: response.data.message,
            });
          }
        })
        .catch((e) => {
          this.$notify({
              type: 'error',
              title: 'Something went wrong!',
              text: e.response.data.message,
          });
          this.loading = false;
          this.$router.replace('/rfq')
        });
    },
    getRFQDetail() {
      this.loading = true
      this.axios
        .post(
          endpoint.RFQ_DETAIL,
          {
            id: this.$route.params.id
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`
            }
          }
        )
        .then((response) => {
          if (response.data.response_code === "0000") {
            this.rfq = response.data.data;
            this.loading = false;
          } else {
            this.$notify({
                type: 'warn',
                title: 'Warning',
                text: response.data.message,
            });
            this.loading = false;
          }
        })
        .catch((e) => {
          this.$notify({
              type: 'error',
              title: 'Something went wrong!',
              text: e.response.data.message,
          });
          this.loading = false;
          this.$router.replace('/rfq')
        });
    },
    inputQtyAvail(line_id, ref) {
      for(var i = 0; i < this.order_line.length; i++) {
          if(this.order_line[i].purchase_line_id == line_id) {
              this.order_line.splice(i, 1);
              break;
          }
      }
      this.order_line.push({
        'purchase_line_id': line_id,
        'qty_avail': this.$refs[ref].value
      })
    },
    confirm() {
      var month = null
      var day = null
      if (this.receipt_date !== null) {
        month = this.receipt_date.getMonth() >= 9 ? (this.receipt_date.getMonth() + 1) : '0' + (this.receipt_date.getMonth() + 1)
        day = this.receipt_date.getDate() < 10 ? ('0' + this.receipt_date.getDate()) : this.receipt_date.getDate()
      }
      this.loading = true
      this.rfq.order_line.forEach(line => {
        this.order_line.push({
          'purchase_line_id': line.id,
          'qty_avail': this.$refs['qtyAvail'+line.id].value,
          'bid_price': this.$refs['bidPrice'+line.id].value,
        })
      });
      this.axios
      .post(
        endpoint.RFQ_CONFIRM,
        {
          id: this.$route.params.id,
          order_line: this.order_line,
          date_planned: this.receipt_date !== null ? this.receipt_date.getFullYear() + '-' + month + '-' + day : null
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        }
      )
      .then((response) => {
        if (response.data.response_code === "0000") {
          // success
          this.loading = false
          this.edit = false
          this.getRFQDetail()
          this.$notify({
            type: 'success',
            title: 'Voila!',
            text: 'Successfully confirm RFQ',
          });
        } else {
          // sys error
          this.loading = false;
          this.$notify({
            type: 'error',
            title: 'Oops!',
            text: response.data.message,
          });
        }
      })
      .catch((e) => {
        this.loading = false;
        this.$notify({
            type: 'error',
            title: 'Error!',
            text: e.response.data.message,
          });
      });
    },
    download () {
        this.loading = true
        this.axios
            .post(
            endpoint.DOWNLOAD,
            {
                res_id: this.$route.params.id,
                template_report: 'bh_purchase_request.action_report_po'
            },
            {
                headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`
                }
            }
            )
            .then((response) => {
            if (response.data.response_code === "0000") {
                const linkSource = `data:application/pdf;base64,${response.data.data.file}`;
                const downloadLink = document.createElement("a");
                const fileName = this.rfq.name + ".pdf";
                downloadLink.href = linkSource;
                downloadLink.download = fileName;
                downloadLink.click();
            } else {
                if (response.data.response_code !== "4000"){
                this.$notify({
                    type: 'warn',
                    title: 'Warning',
                    text: response.data.message,
                });
                }
            }
            this.loading = false
            })
            .catch((e) => {
            this.loading = false;
            this.$notify({
                type: 'error',
                title: 'Something went wrong!',
                text: e.response.data.message,
                });
            });
        },
  },
  mounted() {
    this.getRFQDetail();
  }
};
</script>

<style>
 .alert-text {
  font-size: 12px !important;
 }
</style>